import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, List, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import ReactMarkdown from 'react-markdown';
import { useLanguage } from '../context/LanguageContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const UserGuidePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [guideSections, setGuideSections] = useState([]);
  const [showIndex, setShowIndex] = useState(true);

  useEffect(() => {
    // Fetch User Guide content from public folder
    setLoading(true);
    fetch('/documentation/User_Guide.md')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load User Guide');
        return res.text();
      })
      .then(text => {
        setContent(text);
        parseGuideContent(text);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading User Guide:', err);
        setContent('# Error Loading User Guide\n\nPlease try again later.');
        setLoading(false);
      });
  }, []);

  const parseGuideContent = (text) => {
    // Parse content into sections by headings
    const sections = [];
    const lines = text.split('\n');
    let currentMainSection = null;
    let currentSubSection = null;
    let currentContent = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // H2 - Main section heading
      if (line.match(/^##\s+/) && !line.match(/^###/)) {
        // Save previous subsection if exists
        if (currentSubSection && currentMainSection) {
          currentMainSection.subsections.push({
            ...currentSubSection,
            content: currentContent.join('\n').trim()
          });
          currentSubSection = null;
          currentContent = [];
        }
        
        // Save previous main section
        if (currentMainSection) {
          sections.push(currentMainSection);
        }
        
        // Start new main section
        const title = line.replace(/^##\s+/, '');
        currentMainSection = {
          id: `section-${sections.length}`,
          title: title,
          subsections: [],
          content: []
        };
      }
      // H3 - Subsection heading
      else if (line.match(/^###\s+/)) {
        // Save previous subsection if exists
        if (currentSubSection && currentMainSection) {
          currentMainSection.subsections.push({
            ...currentSubSection,
            content: currentContent.join('\n').trim()
          });
          currentContent = [];
        }
        
        // Start new subsection
        const title = line.replace(/^###\s+/, '');
        currentSubSection = {
          id: `subsection-${sections.length}-${currentMainSection?.subsections.length || 0}`,
          title: title
        };
      }
      // Content
      else if (line.trim()) {
        if (currentSubSection) {
          currentContent.push(line);
        } else if (currentMainSection) {
          currentMainSection.content.push(line);
        }
      }
    }
    
    // Save last subsection
    if (currentSubSection && currentMainSection) {
      currentMainSection.subsections.push({
        ...currentSubSection,
        content: currentContent.join('\n').trim()
      });
    }
    
    // Save last main section
    if (currentMainSection) {
      sections.push(currentMainSection);
    }

    setGuideSections(sections.filter(s => s.title && s.title.trim()));
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white border-b-2 border-cyan-500 shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="flex items-center gap-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50"
              >
                <ArrowLeft className="h-4 w-4" />
                {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
              </Button>
              <div className="flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-cyan-600" />
                <h1 className="text-2xl font-bold text-gray-800">
                  {language === 'ar' ? 'دليل المستخدم' : 'User Guide'}
                </h1>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowIndex(!showIndex)}
              className="flex items-center gap-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50"
            >
              <List className="h-4 w-4" />
              {showIndex ? (language === 'ar' ? 'إخفاء الفهرس' : 'Hide Index') : (language === 'ar' ? 'إظهار الفهرس' : 'Show Index')}
            </Button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="container mx-auto px-6 py-3">
        <nav className="text-sm text-gray-600">
          <span className="hover:text-cyan-600 cursor-pointer" onClick={() => navigate('/')}>
            {language === 'ar' ? 'الرئيسية' : 'Home'}
          </span>
          <span className="mx-2">/</span>
          <span className="text-cyan-600 font-medium">
            {language === 'ar' ? 'دليل المستخدم' : 'User Guide'}
          </span>
        </nav>
      </div>

      {/* Content */}
      <main className="container mx-auto px-6 py-8 pb-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Index Sidebar */}
            {showIndex && guideSections.length > 0 && (
              <aside className="w-80 flex-shrink-0">
                <div className="sticky top-24 bg-white rounded-xl shadow-lg p-6 max-h-[calc(100vh-150px)] overflow-y-auto">
                  <h2 className="text-lg font-bold text-cyan-700 mb-4 flex items-center gap-2">
                    <List className="h-5 w-5" />
                    {language === 'ar' ? 'الفهرس' : 'Table of Contents'}
                  </h2>
                  <nav className="space-y-1">
                    {guideSections.map((section, index) => (
                      <div key={index}>
                        <button
                          onClick={() => scrollToSection(section.id)}
                          className="block w-full text-left py-2 px-3 rounded-lg transition-colors hover:bg-cyan-50 hover:text-cyan-700 font-semibold text-cyan-600 text-sm"
                        >
                          {section.title}
                        </button>
                        {section.subsections && section.subsections.length > 0 && (
                          <div className="ml-4 space-y-1 mt-1">
                            {section.subsections.map((subsection, subIndex) => (
                              <button
                                key={subIndex}
                                onClick={() => scrollToSection(subsection.id)}
                                className="block w-full text-left py-1.5 px-3 rounded-lg transition-colors hover:bg-cyan-50 hover:text-cyan-700 text-gray-600 text-xs"
                              >
                                {subsection.title}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </nav>
                </div>
              </aside>
            )}

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
                <h1 className="text-4xl font-bold text-cyan-700 mb-8 pb-3 border-b-2 border-cyan-200">
                  {language === 'ar' ? 'دليل المستخدم الشامل' : 'Comprehensive User Guide'}
                </h1>

                {guideSections.map((section, sectionIndex) => (
                  <div key={sectionIndex} id={section.id} className="mb-8 scroll-mt-24">
                    <h2 className="text-2xl font-semibold text-cyan-600 mb-4">
                      {section.title}
                    </h2>
                    
                    {/* Main section content if any */}
                    {section.content && section.content.length > 0 && (
                      <div className="mb-4 text-gray-700">
                        <ReactMarkdown>{section.content.join('\n')}</ReactMarkdown>
                      </div>
                    )}
                    
                    {/* Subsections as accordion */}
                    {section.subsections && section.subsections.length > 0 && (
                      <Accordion type="single" collapsible className="space-y-2">
                        {section.subsections.map((subsection, subIndex) => (
                          <AccordionItem 
                            key={subIndex} 
                            value={`item-${sectionIndex}-${subIndex}`}
                            id={subsection.id}
                            className="border border-gray-200 rounded-lg px-4 hover:border-cyan-300 transition-colors scroll-mt-24"
                          >
                            <AccordionTrigger className="text-left font-medium text-gray-800 hover:text-cyan-600 py-4 hover:no-underline">
                              <span className="flex items-start gap-2">
                                <ChevronDown className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                                <span>{subsection.title}</span>
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="text-gray-700 pb-4 pt-2 pl-7">
                              <ReactMarkdown
                                components={{
                                  p: ({node, ...props}) => <p className="mb-3 leading-relaxed" {...props} />,
                                  ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 mb-3" {...props} />,
                                  ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-2 mb-3" {...props} />,
                                  li: ({node, ...props}) => <li className="ml-4" {...props} />,
                                  strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                                  a: ({node, ...props}) => <a className="text-cyan-600 hover:text-cyan-700 underline" {...props} />,
                                  h4: ({node, ...props}) => <h4 className="font-semibold text-gray-900 mt-4 mb-2" {...props} />,
                                }}
                              >
                                {subsection.content}
                              </ReactMarkdown>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Quick Links Footer */}
      <footer className="container mx-auto px-6 py-8 max-w-7xl">
        <div className="bg-cyan-50 rounded-lg p-6 border border-cyan-200">
          <h3 className="text-lg font-semibold text-cyan-800 mb-3">
            {language === 'ar' ? 'مصادر مفيدة أخرى' : 'Other Helpful Resources'}
          </h3>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/faq')}
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-100"
            >
              {language === 'ar' ? 'الأسئلة الشائعة' : 'FAQs'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/privacy-policy')}
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-100"
            >
              {language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/terms')}
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-100"
            >
              {language === 'ar' ? 'شروط الاستخدام' : 'Terms of Use'}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserGuidePage;
